/**
 * /app/{slug}/compliance — Compliance Command Center.
 *
 * Single consolidated dashboard showing next-due-dates for every Indian
 * SMB statutory compliance obligation. HR-admin only (mirrors backend
 * RBAC: company_admin + hr_manager on GET /api/v1/compliance/dashboard).
 *
 * Card inventory (11 items — all server-computed):
 *   1.  PF/ECR monthly            — due 15th of next month
 *   2.  ESI monthly               — due 15th of next month
 *   3.  Professional Tax monthly  — due 15th of next month
 *   4.  TDS 24Q quarterly         — due 31st of month after quarter end
 *   5.  GSTR-1 monthly            — due 11th of next month
 *   6.  GSTR-3B monthly           — due 20th of next month
 *   7.  Bonus Form D annual (FY)  — due 30 April (30 days after FY end)
 *   8.  POSH §21 annual           — due 31 January
 *   9.  S&E annual return         — due 31 January
 *  10.  Maternity §11A annual     — due 31 May
 *  11.  LWF biannual              — due 15 July / 15 January
 *
 * Status badge logic (server-side, migration 103 closed the
 * "Generated ≠ Filed" gap):
 *   "filed"     — HR recorded a real-world filing acknowledgment (ack
 *                 number + date) for the current period; the card shows
 *                 the ack details with a subtle "Update" affordance
 *   "generated" — export/artefact generated this period, no ack yet
 *   "overdue"   — due date in the past, neither of the above
 *   "pending"   — due date in the future, neither of the above
 *
 * "Mark filed" (generated/pending/overdue items) opens a dialog:
 * filed-on date (default today) + ack reference + optional notes →
 * POST /api/v1/compliance/filings (upsert per obligation+period; the
 * period key is the item's next_due_date) → invalidate the dashboard
 * query → toast.
 *
 * Design: 4-col card grid (sm: 2-col). Each card shows
 *   title | statute_ref | cadence badge | next_due_date | status badge |
 *   filing details (when filed) | actions
 * The generator action links to generator_url (same SPA — Link).
 *
 * No date-picker or period selector — all period maths live server-side
 * anchored to today; HR sees the current obligation set without input.
 */

import { useState } from "react";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Clock,
  FileCheck2,
  PencilLine,
  ShieldX,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { isHRAdmin } from "@/lib/nav";
import { complianceApi, toApiError } from "@/lib/api";
import type { ComplianceItem, ComplianceStatus } from "@/lib/api/compliance";
import {
  COMPLIANCE_STATUS_LABEL,
  canMarkFiledToday,
  filableFrom,
  filableInPeriod,
  complianceStatusLabel,
  filedOnProblem,
} from "@/lib/compliance-status";
import { PageHeader } from "@/components/shared/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/format";
import { useTenantSlug } from "@/lib/use-tenant-slug";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/compliance",
)({
  component: ComplianceRoute,
});

// ---------------------------------------------------------------------------
// Query key + fetcher hook
// ---------------------------------------------------------------------------

const COMPLIANCE_QUERY_KEY = ["compliance", "dashboard"] as const;

function useComplianceDashboard() {
  return useQuery({
    queryKey: COMPLIANCE_QUERY_KEY,
    queryFn: ({ signal }) => complianceApi.getComplianceDashboard(signal),
    staleTime: 5 * 60 * 1000, // 5 minutes — due dates don't change per second
  });
}

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

// "filed" now means TRULY filed (HR recorded the authority's
// acknowledgment); "generated" is the old export-ready state. An overdue
// item whose artefact was generated reads "Generated — not filed".
const STATUS_LABEL = COMPLIANCE_STATUS_LABEL;

const STATUS_VARIANT: Record<
  ComplianceStatus,
  "default" | "secondary" | "destructive" | "outline"
> = {
  filed: "default",
  generated: "outline",
  pending: "secondary",
  overdue: "destructive",
};

// Distinct look per status: filed keeps the green/primary treatment,
// generated gets blue so it never reads as "done".
const STATUS_CLASS: Partial<Record<ComplianceStatus, string>> = {
  generated:
    "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300",
};

function StatusBadge({
  status,
  label,
}: Readonly<{ status: ComplianceStatus; label?: string }>) {
  const icon =
    status === "filed" ? (
      <CheckCircle2 className="h-3 w-3" />
    ) : status === "generated" ? (
      <FileCheck2 className="h-3 w-3" />
    ) : status === "overdue" ? (
      <AlertTriangle className="h-3 w-3" />
    ) : (
      <Clock className="h-3 w-3" />
    );
  return (
    <Badge
      variant={STATUS_VARIANT[status]}
      className={cn("gap-1 text-xs", STATUS_CLASS[status])}
      data-testid={`compliance-status-${status}`}
    >
      {icon}
      {label ?? STATUS_LABEL[status]}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Mark-filed dialog
// ---------------------------------------------------------------------------

/** Local YYYY-MM-DD (not UTC — an IST HR admin's "today" must be today). */
function todayLocalISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

interface MarkFiledDialogProps {
  item: ComplianceItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function MarkFiledDialog({
  item,
  open,
  onOpenChange,
}: Readonly<MarkFiledDialogProps>) {
  const qc = useQueryClient();
  const isUpdate = item.filing != null;

  const [filedOn, setFiledOn] = useState(
    item.filing?.filed_on ?? todayLocalISO(),
  );
  const [ackReference, setAckReference] = useState(
    item.filing?.ack_reference ?? "",
  );
  const [notes, setNotes] = useState(item.filing?.notes ?? "");

  const problem = filedOnProblem(item, filedOn, todayLocalISO());
  const record = useMutation({
    mutationFn: () =>
      complianceApi.recordComplianceFiling({
        obligation: item.id,
        period: item.next_due_date,
        filed_on: filedOn,
        ack_reference: ackReference.trim(),
        notes: notes.trim(),
      }),
    onSuccess: async () => {
      toast.success(
        isUpdate
          ? "Filing acknowledgment updated."
          : "Filing recorded — marked as filed for this period.",
      );
      onOpenChange(false);
      await qc.invalidateQueries({ queryKey: COMPLIANCE_QUERY_KEY });
    },
    onError: (err) => {
      toast.error(toApiError(err).message || "Could not record the filing.");
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isUpdate ? "Update Filing Record" : "Mark as Filed"}
          </DialogTitle>
          <DialogDescription>
            {item.title}
            {item.period_label ? ` (${item.period_label})` : ""} — cycle due{" "}
            {fmtDueDate(item.next_due_date)}. Record the date you filed with
            the authority and the acknowledgment number the portal issued.
          </DialogDescription>
        </DialogHeader>

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!filedOn || problem || record.isPending) return;
            record.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor={`filed-on-${item.id}`}>Filed On</Label>
            <Input
              id={`filed-on-${item.id}`}
              type="date"
              required
              value={filedOn}
              min={filableFrom(item)}
              max={todayLocalISO()}
              onChange={(e) => setFiledOn(e.target.value)}
              aria-invalid={!!problem}
              data-testid={`filing-filed-on-${item.id}`}
            />
            {problem && filedOn && (
              <p className="text-xs text-destructive">{problem}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`ack-ref-${item.id}`}>
              Acknowledgment Reference{" "}
              <span className="font-normal text-muted-foreground">
                (TRRN / ARN / Token — Optional)
              </span>
            </Label>
            <Input
              id={`ack-ref-${item.id}`}
              value={ackReference}
              maxLength={128}
              placeholder="e.g. 2032612345678"
              onChange={(e) => setAckReference(e.target.value)}
              data-testid={`filing-ack-${item.id}`}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`notes-${item.id}`}>
              Notes{" "}
              <span className="font-normal text-muted-foreground">
                (Optional)
              </span>
            </Label>
            <Textarea
              id={`notes-${item.id}`}
              value={notes}
              maxLength={500}
              rows={3}
              placeholder="Anything worth remembering about this filing."
              onChange={(e) => setNotes(e.target.value)}
              data-testid={`filing-notes-${item.id}`}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={record.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!filedOn || !!problem || record.isPending}
              data-testid={`filing-submit-${item.id}`}
            >
              {record.isPending
                ? "Saving…"
                : isUpdate
                  ? "Update Record"
                  : "Record Filing"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// ComplianceCard
// ---------------------------------------------------------------------------

function fmtDueDate(iso: string): string {
  return formatDate(iso);
}

interface ComplianceCardProps {
  item: ComplianceItem;
  tenantSlug: string;
}

function ComplianceCard({ item, tenantSlug }: Readonly<ComplianceCardProps>) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const dueDateLabel = fmtDueDate(item.next_due_date);

  // Resolve the generator_url — an absolute path like
  // "/app/my-company/payroll/statutory"; use Link for in-app nav.
  const generatorHref = item.generator_url;

  const cardBorder =
    item.status === "overdue"
      ? "border-destructive/50"
      : item.status === "filed"
        ? "border-green-500/40"
        : item.status === "generated"
          ? "border-blue-500/40"
          : "";

  return (
    <Card
      className={cn("flex min-w-0 flex-col", cardBorder)}
      data-testid={`compliance-card-${item.id}`}
    >
      <CardHeader className="pb-3">
        {/* flex-wrap + a minimum text width: a long cadence badge
            ("Annual (Calendar Year)") moves under the title instead of
            squeezing the statute to one word per line. */}
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-[10rem] flex-1">
            <CardTitle className="truncate text-sm font-semibold leading-tight" title={item.title}>
              {item.title}
            </CardTitle>
            <CardDescription className="mt-0.5 text-xs">
              {item.statute_ref}
            </CardDescription>
          </div>
          <Badge variant="outline" className="shrink-0 text-xs capitalize">
            {item.cadence}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="mt-auto space-y-3">
        {/* Due date + status */}
        <div className="flex items-center justify-between gap-2 rounded-md bg-muted/40 px-3 py-2">
          <div>
            <p className="text-xs text-muted-foreground">
              {item.status === "overdue" ? "Was due" : "Next due"}
            </p>
            <p className="text-sm font-medium">{dueDateLabel}</p>
            {item.period_label && (
              <p className="text-xs text-muted-foreground">
                For {item.period_label}
              </p>
            )}
          </div>
          <StatusBadge status={item.status} label={complianceStatusLabel(item)} />
        </div>

        {/* Filing acknowledgment (only when truly filed) */}
        {item.filing && (
          <div
            className="flex items-start justify-between gap-2 rounded-md border border-green-500/30 bg-green-50/50 px-3 py-2 dark:bg-green-950/40"
            data-testid={`compliance-filing-${item.id}`}
          >
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">
                Filed {fmtDueDate(item.filing.filed_on)}
                {item.filing.filed_by && <> by {item.filing.filed_by}</>}
              </p>
              <p className="truncate font-mono text-sm">
                {item.filing.ack_reference || "No ack reference"}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 shrink-0 gap-1 px-2 text-xs text-muted-foreground"
              onClick={() => setDialogOpen(true)}
              data-testid={`compliance-update-filing-${item.id}`}
            >
              <PencilLine className="h-3 w-3" />
              Update
            </Button>
          </div>
        )}

        {/* Mark filed — the write that closes the loop. Not before the
            earliest filing date: a return is filed for a finished period;
            a payroll remittance can be paid inside its wage month. */}
        {!item.filing && canMarkFiledToday(item, todayLocalISO()) && (
          <Button variant="outline"
            size="sm"
            className="w-full gap-1.5"
            onClick={() => setDialogOpen(true)}
            data-testid={`compliance-mark-filed-${item.id}`}
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            Mark Filed
          </Button>
        )}
        {!item.filing && !canMarkFiledToday(item, todayLocalISO()) && (
          <p className="text-center text-xs text-muted-foreground">
            {filableInPeriod(item)
              ? `Can be marked filed from ${fmtDueDate(filableFrom(item) ?? "")}.`
              : `Can be marked filed once the period ends (${fmtDueDate(item.period_end ?? "")}).`}
          </p>
        )}

        {/* Generator link — always present */}
        <Button
          asChild
          variant="outline"
          size="sm"
          className="w-full gap-1.5"
          data-testid={`compliance-go-${item.id}`}
        >
          <Link
            to={generatorHref as string}
            params={{ tenantSlug }}
          >
            Go to Generator
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </Button>
      </CardContent>

      {dialogOpen && (
        <MarkFiledDialog
          item={item}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
        />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Role gate (UX mirror of backend RBAC)
// ---------------------------------------------------------------------------

function ComplianceRoleGate({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  const tenantSlug = useTenantSlug();
  const { user } = useAuth();
  if (isHRAdmin(user?.role)) return <>{children}</>;
  return (
    <>
      <PageHeader
        title="Compliance Calendar"
        description="Indian SMB statutory compliance calendar — next due dates at a glance."
      />
      <Card>
        <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent text-accent-foreground">
            <ShieldX className="h-6 w-6" />
          </span>
          <div className="space-y-1">
            <p className="text-base font-medium text-gray-900">
              Compliance is managed by HR admins
            </p>
            <p className="max-w-md text-sm text-muted-foreground">
              Only company admins and HR managers can view the compliance
              dashboard. Ask your HR admin if you need access.
            </p>
          </div>
          <Button asChild variant="outline" className="gap-1.5">
            <Link to="/app/$tenantSlug" params={{ tenantSlug }}>
              Back to Dashboard
            </Link>
          </Button>
        </CardContent>
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main route component
// ---------------------------------------------------------------------------

function ComplianceRoute() {
  const { tenantSlug } = useParams({ strict: false }) as {
    tenantSlug: string;
  };

  const { data, isPending, isError, error } = useComplianceDashboard();

  const items = data?.items ?? [];

  return (
    <ComplianceRoleGate>
      <PageHeader
        title="Compliance Calendar"
        description="Indian SMB statutory compliance calendar — next due dates at a glance. Filed = acknowledgment recorded · Generated = export ready · red = overdue · grey = pending."
      />

      {/* Error state */}
      {isError && (
        <Alert variant="destructive" className="mt-4">
          <AlertTitle>Failed to load compliance data</AlertTitle>
          <AlertDescription>
            {(error as Error)?.message ?? "An unexpected error occurred."}
          </AlertDescription>
        </Alert>
      )}

      {/* Summary strip — counts */}
      {!isPending && !isError && items.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-3">
          {(
            ["overdue", "pending", "generated", "filed"] as ComplianceStatus[]
          ).map((s) => {
            const count = items.filter((i) => i.status === s).length;
            if (count === 0) return null;
            return (
              <div
                key={s}
                className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm"
              >
                <StatusBadge status={s} />
                <span className="font-semibold">{count}</span>
                <span className="text-muted-foreground">
                  obligation{count !== 1 ? "s" : ""}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Card grid */}
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {isPending
          ? Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-48" />
            ))
          : items.map((item) => (
              <ComplianceCard
                key={item.id}
                item={item}
                tenantSlug={tenantSlug}
              />
            ))}
      </div>

      {/* Empty state (no items — shouldn't happen but defensive) */}
      {!isPending && !isError && items.length === 0 && (
        <Card className="mt-4">
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <CheckCircle2 className="h-10 w-10 text-muted-foreground" />
            <p className="text-base font-medium">No obligations found</p>
            <p className="text-sm text-muted-foreground">
              Nothing is due for your company right now. If you expected
              filings here, reload the page or contact support.
            </p>
          </CardContent>
        </Card>
      )}
    </ComplianceRoleGate>
  );
}
